/**
 * Session state.
 *
 * The important thing this module gets right is that **"not signed in" and "we
 * could not ask" are different states**, and it refuses to collapse them.
 *
 * The backend takes the same position deliberately: an outage at the identity
 * provider is reported as a fault rather than as a logout, "so nobody spends
 * the morning signing in repeatedly while the real problem goes unlooked-at".
 * A frontend that maps every failed `whoami` to "anonymous" bounces a signed-in
 * customer to a login screen during an Ory blip, they sign in, it bounces them
 * again, and the support ticket says "login is broken" rather than "Ory is
 * down".
 */

import type { FrontendApi, Session } from '@ory/client-fetch'
import { stepUpRequired } from './flow'
import type { StepUp } from './flow'

/** Who the caller is, as far as Ory is concerned. */
export interface Identity {
  /** Ory's identity id. The backend stores this as an external reference. */
  readonly id: string
  /** Best available human label — an email, a username, or the id. */
  readonly label: string
  /**
   * The identity's email, when the trait genuinely is one.
   *
   * Separate from `label`, which falls back through usernames to the raw id. A
   * registration form prefilling `label` would put an Ory UUID in an email box
   * on any tenant whose schema does not use email, and the customer would have
   * to notice and clear it. Absent is a better answer than wrong.
   */
  readonly email?: string
  /** Two-letter monogram for the rail avatar. */
  readonly initials: string
  /** Whether Ory considers the session to satisfy its highest AAL. */
  readonly verified: boolean
}

export type SessionState =
  | { readonly status: 'loading' }
  | { readonly status: 'authenticated'; readonly identity: Identity }
  | { readonly status: 'anonymous' }
  /**
   * Signed in, and not strongly enough (#98).
   *
   * A THIRD state, for the same reason `unavailable` is a second one. Ory
   * answers `401` when there is no session and `403` when there IS one that has
   * not reached the assurance level the tenant requires — so the 403 branch is
   * never a shared case with 401, it is only ever a customer who is signed in
   * and needs to step up. Mapping it to `anonymous` sent them to a login screen,
   * they signed in, and it refused them again, because signing in with the same
   * factor does not raise the assurance level. That is #47's loop reached from
   * the other end.
   *
   * There is no identity here: `toSession` refused, so we have no session to
   * read one from. That is the honest shape — we know somebody is there and not
   * who.
   */
  | { readonly status: 'step_up'; readonly stepUp: StepUp }
  /** Ory could not be reached or answered unusably. NOT the same as anonymous. */
  | { readonly status: 'unavailable'; readonly reason: string }

/**
 * Extract a display label from an Ory identity.
 *
 * Ory identity traits are schema-defined and therefore arbitrary. Rather than
 * assume `traits.email` exists, this walks a short preference list and falls
 * back to the id — a rail that renders `undefined` because the schema changed
 * is a worse outcome than one that renders an opaque identifier.
 */
export function labelOf(session: Session): string {
  const traits = session.identity?.traits as Record<string, unknown> | undefined
  if (traits !== undefined) {
    for (const key of ['email', 'username', 'name', 'handle'] as const) {
      const v = traits[key]
      if (typeof v === 'string' && v.trim() !== '') return v.trim()
    }
    // `name` is often an object: { first, last }.
    const name = traits['name']
    if (typeof name === 'object' && name !== null) {
      const n = name as Record<string, unknown>
      const parts = [n['first'], n['last']].filter((p): p is string => typeof p === 'string')
      if (parts.length > 0) return parts.join(' ')
    }
  }
  return session.identity?.id ?? 'Signed in'
}

/** `mia@example.com` → `MI`; `Mia Andersen` → `MA`. */
export function initialsOf(label: string): string {
  const local = label.includes('@') ? (label.split('@')[0] ?? label) : label
  const words = local.split(/[\s._-]+/).filter((w) => w !== '')
  if (words.length >= 2) {
    return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase()
  }
  return (words[0] ?? local).slice(0, 2).toUpperCase()
}

/**
 * The `email` trait, only when it is one.
 *
 * Deliberately stricter than `labelOf`: that function's job is to always
 * produce something printable, and this one's is to produce nothing rather than
 * something wrong.
 */
export function emailOf(session: Session): string | undefined {
  const traits = session.identity?.traits as Record<string, unknown> | undefined
  const v = traits?.['email']
  if (typeof v !== 'string') return undefined
  const email = v.trim()
  // Not a validator — the backend and Ory both hold the real rule. This only
  // establishes that the trait is an address rather than a display name, which
  // is the question being asked.
  return email.includes('@') ? email : undefined
}

export function identityOf(session: Session): Identity {
  const label = labelOf(session)
  const email = emailOf(session)
  return {
    id: session.identity?.id ?? '',
    label,
    ...(email === undefined ? {} : { email }),
    initials: initialsOf(label),
    // Ory reports the assurance level the session actually reached. `aal2`
    // means a second factor was satisfied. This is presentational only —
    // nothing in this app may gate an action on it, because that would be
    // authorization and authorization is the backend's (§6, invariant 8).
    verified: session.authenticator_assurance_level === 'aal2',
  }
}

/**
 * Ask Ory who the caller is.
 *
 * A **401** is a real answer — nobody is signed in.
 *
 * A **403 is not the same answer**, and this used to say it was (#98). Ory
 * returns it when a session EXISTS and has not reached the required assurance
 * level, so the branch was only ever reached for a customer who was signed in.
 * Sending them to `/sign-in` produced the #47 loop from the other end: they sign
 * in, the level does not move, and it refuses again.
 *
 * Anything else is a failure to get an answer, and is reported as such.
 */
export async function fetchSession(ory: FrontendApi): Promise<SessionState> {
  try {
    const session = await ory.toSession()
    // Ory can return a session that exists but is not active (e.g. expired
    // mid-request). Treating that as signed in would render the app for
    // someone the backend will refuse on the next call.
    if (session.active === false) return { status: 'anonymous' }
    return { status: 'authenticated', identity: identityOf(session) }
  } catch (err: unknown) {
    const status = statusOf(err)
    if (status === 401) return { status: 'anonymous' }

    if (status === 403) {
      // Read the BODY, not just the status. Ory identifies this as
      // `session_aal2_required`; branching on 403 alone would sweep in any other
      // 403 the SDK might surface, which is how the old line got it wrong in the
      // first place. `stepUpRequired` already does exactly this reading, and
      // already prefers aal2 over refresh when both appear.
      const stepUp = await stepUpRequired(err)
      if (stepUp !== null) return { status: 'step_up', stepUp }
      // A 403 that does not identify itself as a step-up is an answer we do not
      // understand, and guessing "signed out" is the guess that broke this.
      return {
        status: 'unavailable',
        reason: 'the identity provider refused to say who you are (HTTP 403)',
      }
    }

    return {
      status: 'unavailable',
      reason:
        status === undefined
          ? `could not reach the identity provider (${describe(err)})`
          : `the identity provider returned HTTP ${status}`,
    }
  }
}

/** Pull an HTTP status off whatever shape the SDK threw. */
function statusOf(err: unknown): number | undefined {
  if (typeof err !== 'object' || err === null) return undefined
  const e = err as { response?: { status?: unknown }; status?: unknown }
  if (typeof e.response?.status === 'number') return e.response.status
  if (typeof e.status === 'number') return e.status
  return undefined
}

function describe(err: unknown): string {
  if (err instanceof Error) return err.message
  return String(err)
}
